import React from 'react';
import {
  Card,
  CardBody,
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  Spinner,
  SimpleGrid,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { FaUserAlt } from 'react-icons/fa';
import Select from 'react-select';

const AdditionalFieldsCard = ({
  addUser,
  setAddUser,
  subDivisions,
  selectStyles,
  loadingFileTypes,
  fileTypesOptions,
  talukas,
  loadingTalukas,
  role,
}) => {
  const modalTextColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const labelColor = useColorModeValue('gray.700', 'gray.200');
  const inputBg = useColorModeValue('white', 'gray.700');
  const inputTextColor = useColorModeValue('gray.800', 'white');
  const inputPlaceholderColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBorderColor = useColorModeValue('blue.400', 'blue.300');
  const focusBorderColor = useColorModeValue('blue.500', 'blue.400');
  const cardBg = useColorModeValue('white', 'gray.800');

  if (!role || role === 'admin') return null;

  return (
    <Card
      mb={6}
      boxShadow="sm"
      border="1px solid"
      borderColor={borderColor}
      bg={cardBg}
    >
      <CardBody>
        <VStack align="start" spacing={4}>
          <HStack spacing={2}>
            <Icon as={FaUserAlt} color="orange.500" />
            <Text fontSize="lg" fontWeight="semibold" color={modalTextColor}>
              Additional Information
            </Text>
          </HStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                Office Name
              </FormLabel>
              <Input
                value={addUser.nameOfOffice || ''}
                onChange={(e) =>
                  setAddUser((prev) => ({
                    ...prev,
                    nameOfOffice: e.target.value,
                  }))
                }
                placeholder="Enter office name"
                bg={inputBg}
                borderColor={borderColor}
                color={inputTextColor}
                _placeholder={{ color: inputPlaceholderColor }}
                _hover={{ borderColor: hoverBorderColor }}
                _focus={{
                  borderColor: focusBorderColor,
                  boxShadow: `0 0 0 1px ${focusBorderColor}`,
                }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                Designation
              </FormLabel>
              <Input
                value={addUser.designation || ''}
                onChange={(e) =>
                  setAddUser((prev) => ({
                    ...prev,
                    designation: e.target.value,
                  }))
                }
                placeholder="Enter designation"
                bg={inputBg}
                borderColor={borderColor}
                color={inputTextColor}
                _placeholder={{ color: inputPlaceholderColor }}
                _hover={{ borderColor: hoverBorderColor }}
                _focus={{
                  borderColor: focusBorderColor,
                  boxShadow: `0 0 0 1px ${focusBorderColor}`,
                }}
              />
            </FormControl>
          </SimpleGrid>

          {role === 'subDivisionalOfficer' && (
            <FormControl isRequired w="full">
              <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                Sub-Division
              </FormLabel>
              <Select
                options={subDivisions.map((sd) => ({
                  value: sd._id,
                  label: sd.name,
                }))}
                styles={selectStyles}
                value={subDivisions
                  .filter((sd) => sd._id === addUser.subDivision)
                  .map((sd) => ({ value: sd._id, label: sd.name }))}
                onChange={(option) =>
                  setAddUser((prev) => ({
                    ...prev,
                    subDivision: option?.value || '',
                  }))
                }
                placeholder="Select sub-division"
              />
            </FormControl>
          )}

          {role === 'talukaUser' && (
            <FormControl isRequired w="full">
              <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                Taluka(s)
              </FormLabel>
              <Select
                options={talukas.map((t) => ({
                  value: t._id,
                  label: t.name,
                }))}
                styles={selectStyles}
                value={talukas
                  .filter((t) => addUser.tahsils?.includes(t._id))
                  .map((t) => ({ value: t._id, label: t.name }))}
                onChange={(options) =>
                  setAddUser((prev) => ({
                    ...prev,
                    tahsils: options ? options.map((opt) => opt.value) : [],
                  }))
                }
                placeholder="Select taluka(s)"
                isMulti
              />
            </FormControl>
          )}

          {(role === 'projectOfficer' || role === 'subDivisionalOfficer') && (
            <FormControl isRequired w="full">
              <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                File Type Permissions
              </FormLabel>
              {loadingFileTypes ? (
                <HStack spacing={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color={subtitleColor}>
                    Loading file types...
                  </Text>
                </HStack>
              ) : fileTypesOptions.length === 0 ? (
                <Alert status="info" size="sm">
                  <AlertIcon />
                  No file types available for selected schemes
                </Alert>
              ) : (
                <Select
                  options={fileTypesOptions}
                  styles={selectStyles}
                  value={fileTypesOptions.filter((ft) =>
                    addUser.fileTypes?.includes(ft.value),
                  )}
                  onChange={(options) =>
                    setAddUser((prev) => ({
                      ...prev,
                      fileTypes: options ? options.map((opt) => opt.value) : [],
                    }))
                  }
                  placeholder="Select file type(s)"
                  isMulti
                />
              )}
            </FormControl>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default AdditionalFieldsCard;
